//////////////////
// IMPORTS
//////////////////
// schema
import * as ISchema from "@schema";
import moment from "moment-timezone";
// libraries
import * as React from "react";
import { connect } from "react-redux";
import { createFragmentContainer } from "react-relay";
// teammate-analytics-card imports
import { IContainer, IProps, IState } from "./TeammateAnalyticsCard.d";
import { teammateAnalyticsCardFragments } from "./TeammateAnalyticsCard.gql";
import * as s from "./TeammateAnalyticsCard.styles";
// other internals
import { H2 } from "@ui";
import { CalendarStatus } from "@components/calendar-status";
import { CalendarTimeZone } from "@components/calendar-time-zone";
import { TeammateHealthAvatar } from "@components/teammate-health-avatar";
// util
import { track, TrackingEvents } from "@util/analytics.util";
import { logger } from "@util/logger.util";
import { isFocusTimeCrawledStatusSynced } from "@util/team-health.util";
// state
import { IReduxState } from "@reducers/root.reducer";
import { snackBarOpen } from "@actions/snack-bar.actions";
import { setActiveViewCalendarProfile } from "@actions/chrome-extension.actions";
import { makeEventSchedulerViewActive, updateCalendarIds } from "@actions/feed-scheduler.actions";
import { invitePersonsToTeam } from "@mutations";
// material-ui
import { ChevronRight } from "@material-ui/icons";
import withStyles from "@material-ui/core/styles/withStyles";

/////////////////////
// COMPONENT
/////////////////////
class TeammateAnalyticsCardCmpt extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);

    this.state = {
      inviteSending: false,
      inviteSent: false,
    };
  }

  // ~-~-~-~-~-~-~-
  // Helpers
  // ~-~-~-~-~-~-~-
  private goToProfile = (calendarId: string) => {
    track(TrackingEvents.TEAM_HEALTH.SUMMARY_GO_TO_PROFILE, { calendarId, from: "THDashboard" });
    this.props.setActiveViewCalendarProfile(calendarId);
  };

  private getName = (person: ISchema.IOrgPerson | null, backupCalendarId: string) => {
    if (!!person && !!person.profile && !!person.profile.familyName && !!person.profile.givenName) {
      return `${person.profile.givenName} ${person.profile.familyName}`;
    } else if (!!person && !!person.profile && !!person.profile.givenName) {
      return `${person.profile.givenName}`;
    } else if (!!person && !!person.primaryCalendarId) {
      return `${person.primaryCalendarId}`;
    } else if (backupCalendarId) {
      return `${backupCalendarId}`;
    } else {
      return `Teammate`;
    }
  };

  private onInviteSuccess = () => {
    this.setState({ inviteSending: false, inviteSent: true });
    this.props.snackBarOpen("Invite sent!");
  };

  private onInviteFailure = () => {
    this.setState({ inviteSending: false });
    logger.error("Failed to send invite from Teammate Health Card");
    this.props.snackBarOpen("Failed to send invite.", "error");
  };

  private sendInvite = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    if (this.state.inviteSending || this.state.inviteSent) {
      e.stopPropagation();
      return;
    }

    const { org, teamId, orgPerson, relay } = this.props;
    this.setState({ inviteSending: true });

    if (teamId && orgPerson) {
      invitePersonsToTeam(
        relay.environment,
        { orgRelayId: org.id, teamId: teamId, personIds: [orgPerson.personId] },
        this.onInviteSuccess,
        this.onInviteFailure,
      );
    }

    // callback
    track(TrackingEvents.TEAM_HEALTH.EXPERIMENT_TEAMMATE_HEALTH_CARD_INVITE_CLICKED, {
      calendarId: this.props.calendarFocusTimeScore.calendarId,
    });
    e.stopPropagation();
  };

  private onClickFindTime = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    const { calendarFocusTimeScore } = this.props;
    const { calendarId } = calendarFocusTimeScore;

    this.props.updateCalendarIds([calendarId]);
    this.props.makeEventSchedulerViewActive(true);

    track(TrackingEvents.SCHEDULING.TEAM_ANALYTICS_CARD_FIND_TIME_CLICKED);

    e.stopPropagation();
  };

  // ~-~-~-~-~-~-~-
  // Render
  // ~-~-~-~-~-~-~-
  private maybeRenderInviteBtn = () => {
    const { orgPerson, classes } = this.props;
    const gateTeamHealth = !!orgPerson && !orgPerson.userId;

    if (!gateTeamHealth) {
      return "";
    }

    return (
      <div className={classes.row}>
        <div
          // disabled={this.state.inviteSending || this.state.inviteSent}
          onClick={this.sendInvite}
          className={`${classes.cardLink} ${
            this.state.inviteSending || this.state.inviteSent ? classes.disabledLink : ""
          }`}
        >
          {this.state.inviteSending
            ? "Sending..."
            : this.state.inviteSent
            ? "Invite Sent"
            : "Invite to Clockwise"}
        </div>
      </div>
    );
  };

  private maybeRenderContent = () => {
    const {
      calendarFocusTimeScore,
      person,
      orgPerson,
      calendarStatus,
      profileTimeZone,
      currentUserTimeZone,
      classes,
    } = this.props;
    const { calendarId } = calendarFocusTimeScore;
    const gateTeamHealth = !!orgPerson && !orgPerson.userId;

    if (gateTeamHealth) {
      return "";
    }

    return (
      <React.Fragment>
        <CalendarStatus
          calendarStatus={calendarStatus}
          profileTimeZone={profileTimeZone}
          person={person}
          calendarId={calendarId}
          hideTimeZone
          style={s.styles.status}
        />
        <div className={classes.row}>
          <div onClick={this.onClickFindTime} className={classes.cardLink}>
            Find Time
          </div>
          {/* compare timezone offsets */}
          {(currentUserTimeZone &&
            profileTimeZone &&
            moment().tz(currentUserTimeZone.timeZone).format("Z") !==
              moment().tz(profileTimeZone.timeZone).format("Z")) ||
          (profileTimeZone && !currentUserTimeZone) ? (
            <CalendarTimeZone
              profileTimeZone={profileTimeZone}
              person={person}
              calendarId={calendarId}
              hideInfoTip={true}
            />
          ) : (
            ""
          )}
        </div>
      </React.Fragment>
    );
  };

  public render() {
    const { calendarFocusTimeScore, orgPerson, style, classes } = this.props;

    // name
    const { calendarId } = calendarFocusTimeScore;
    const name = this.getName(orgPerson, calendarId);

    const isCrawled =
      calendarFocusTimeScore?.crawledStatus &&
      isFocusTimeCrawledStatusSynced(calendarFocusTimeScore.crawledStatus);

    // only clickable for synced
    let cursor = {};
    if (isCrawled) {
      cursor = {
        cursor: "pointer",
      };
    }

    return (
      <div
        style={{ ...s.styles.container, ...style, ...cursor }}
        cw-id="teammate-card"
        onClick={isCrawled ? this.goToProfile.bind(this, calendarId) : undefined}
      >
        <div className={classes.content}>
          <div className={classes.left}>
            <TeammateHealthAvatar
              org={this.props.org}
              key={this.props.calendarFocusTimeScore.calendarId}
              calendarFocusTimeScore={this.props.calendarFocusTimeScore}
              person={this.props.person}
              fastCrawlFinished={this.props.fastCrawlFinished}
              calendarId={this.props.calendarFocusTimeScore.calendarId}
              maxMinutes={this.props.maxMinutes}
              width={s.avatarWidth}
              hideName
            />
          </div>
          <div className={classes.right}>
            <div className={classes.nameContainer}>
              <H2 style={s.styles.h2}>{name}</H2>
              <ChevronRight style={s.styles.chevron} />
            </div>
            {this.maybeRenderContent()}
            {this.maybeRenderInviteBtn()}
          </div>
        </div>
      </div>
    );
  }
}

//////////////////
// REDUX
//////////////////
function mapStateToProps(state: IReduxState, _ownProps: IContainer) {
  return {
    fastCrawlFinished: state.onboarding.fastCrawlFinished,
  };
}

const TeammateAnalyticsCardRedux = connect(mapStateToProps, {
  snackBarOpen,
  setActiveViewCalendarProfile,
  makeEventSchedulerViewActive,
  updateCalendarIds,
})(TeammateAnalyticsCardCmpt);

//////////////////
// WITH STYLES
//////////////////
const TeammateAnalyticsCardStyled = withStyles(s.styles)(TeammateAnalyticsCardRedux);

//////////////////
// RELAY
//////////////////
export const TeammateAnalyticsCard = createFragmentContainer<IContainer>(
  TeammateAnalyticsCardStyled,
  teammateAnalyticsCardFragments,
);
